import React from "react";
import { Stack, Typography, Grid, useTheme, useMediaQuery, Box } from "@mui/material";
import { CardContainerRetro } from "../Container/CardContainerRetro";
import colors from "../../designTokens/colors";
import { Section } from "../Container/Section";
import { SectionTitleWithExp } from "../customTypo/SectionTitleWithExp";
import colorsCustom from "../../designTokens/colorsCustom";
import { useTranslation } from "react-i18next";
import { useThreadPerformanceData } from "../../hooks/useThreadsSelector";
import { TableSkeleton } from "../skeleton/TableSkeleton";
import { PerformanceTable } from "./PerformanceTable";
import { PerformanceBoxPlot } from "./PerformanceBoxPlot";

export const ThreadPerformanceOverview = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  // 반응형 Hook
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));

  // ThreadsContext에서 스레드 성능 데이터 가져오기
  const { boxPlotData, isThreadsLoading, isProcessing, isDataReady, error } =
    useThreadPerformanceData();

  // 로딩 중
  if (isThreadsLoading || isProcessing || !isDataReady) {
    return <TableSkeleton title={t("lifetime.metricStats.title")} />;
  }

  // 데이터 로딩 실패 (Hook에서 isDataLoadFailed가 없으므로 error로 판단)
  if (error && (!boxPlotData || Object.keys(boxPlotData).length === 0)) {
    return (
      <Section>
        <SectionTitleWithExp title={t("lifetime.metricStats.title")} />
        <CardContainerRetro
          p={isMobile ? "24px" : "32px"}
          bg={colorsCustom.paper}
          weight="bold"
          rd={16}
        >
          <Stack alignItems="center" spacing={2}>
            <Typography color="warning.main" textAlign="center" variant="body2">
              ⚠️ {t("error.performanceDataFailed")}
            </Typography>
            <Typography textAlign="center" color="text.disabled">
              {t("error.threadsDataLoadFailed")}
            </Typography>
          </Stack>
        </CardContainerRetro>
      </Section>
    );
  }

  return (
    <Section>
      <SectionTitleWithExp
        title={t("lifetime.metricStats.title")}
        exp={t('lifetime.metricStats.description')}
      />
      <CardContainerRetro
        p={isMobile ? "16 0" : "24 0"}
        w={"100%"}
        bg={colorsCustom.paper}
        weight="bold"
        rd={16}
      >
        <Stack spacing={isMobile ? 1 : 2} sx={{ width: "100%" }}>
          {/* ✅ 에러 경고 표시 (데이터가 있을 때) */}
          {error && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1.5,
                mx: isMobile ? 2 : 3,
                backgroundColor: "#fff3cd",
                borderRadius: 1,
                border: "1px solid #ffeaa7",
              }}
            >
              <Typography sx={{ color: "#856404", fontSize: "12px" }}>
                ⚠️ {t("error.partialDataWarning")}{" "}
                {t("error.showingDefaultData")}
              </Typography>
            </Box>
          )}

          <Grid container>
            <Grid 
              size={{ xs: 12, xl: 6 }}
              sx={{
                borderRight: !isMobile ? `0.5px solid ${colors.gray[3]}` : "none",
                borderBottom: isMobile ? `0.5px solid ${colors.gray[3]}` : "none",
              }}
            >
              <PerformanceTable boxPlotData={boxPlotData} />
            </Grid>
            <Grid size={{ xs: 12, xl: 6 }}>
              <PerformanceBoxPlot boxPlotData={boxPlotData} />
            </Grid>
          </Grid>
        </Stack>
      </CardContainerRetro>
    </Section>
  );
};

export default ThreadPerformanceOverview;
